/**
 * Default Configuration Values
 *
 * Centralized defaults for the Bash system.
 */

/**
 * The workspace-sync receipt, relative to the workspace root.
 *
 * ONE path, ONE writer, ONE reader — shared so tsc holds the pairing. The server's
 * `syncSystemFiles` writes the synced manifest version here IN THE SAME BATCH as the
 * content it delivers (apps/server volume.service.ts); the runner's identity report
 * reads the SAME file to say which manifest version its workspace actually holds
 * (apps/sandbox loaded-identity.ts). The 2026-08-14 falsifier fired because the
 * reader used `.claude/sync-manifest.json` instead — a file the sync never rewrites
 * (zero of 747 migrations reference it), so every runner reported the template-bake
 * version (23) forever and a converge could never clear a 'behind' verdict.
 */
export const WORKSPACE_SYNC_RECEIPT_RELPATH = '.forge/.template-version';
/** Pre-.forge receipt home. Read as fallback and dual-written during the
 *  migration window so not-yet-upgraded runners and a rolled-back server both
 *  keep an accurate receipt; retire once the fleet has rotated. */
export const LEGACY_WORKSPACE_SYNC_RECEIPT_RELPATH = '.claude/.template-version';

/**
 * The most a user may raise their own PER-TURN spend ceiling to, in USD.
 *
 * ONE constant, TWO enforcers — shared so tsc holds the pairing: the server's
 * `normalizeTurnSpendCeilingUsd` (credit.service) is the authority that refuses
 * a bigger figure, and the Settings field mirrors it so the refusal is visible
 * before the round-trip. A second copy would drift into a field that accepts
 * what the server rejects.
 *
 * The FLOOR is deliberately not a constant here: it is "any positive amount",
 * and 0 is refused rather than read as "no limit" (only ops disable the rail,
 * via runtime_config).
 */
export const TURN_SPEND_CEILING_MAX_USD = 100;

/**
 * The smallest per-turn ceiling a user may set, in USD — the smallest one the
 * COLUMN can hold. `user_credit_settings.turn_spend_ceiling_usd` is
 * `numeric(10,2)`, so a sub-cent figure rounds to `0.00` on the way in and then
 * reads back as "unset" to every reader (`v > 0`): the save would report success
 * and the platform default would quietly govern. Refusing it is the honest
 * answer, and it keeps the two enforcers agreeing with the copy beside them.
 */
export const TURN_SPEND_CEILING_MIN_USD = 0.01;

/**
 * The most a user may raise their own DAILY spend ceiling to, in USD.
 *
 * The day governor answers a question the per-turn ceiling cannot: a routine
 * firing hourly stays under a $10 turn ceiling all day and still spends $240.
 * Same two enforcers, same pairing held by tsc — the server's
 * `normalizeDailySpendCeilingUsd` refuses a bigger figure and the Settings field
 * mirrors it. Equal to the per-turn cap since 2026-08-27: $100 is the founder's
 * hard ceiling on every settable limit, on every axis.
 */
export const DAILY_SPEND_CEILING_MAX_USD = 100;

/**
 * The smallest daily ceiling a user may set, in USD — the column's own
 * resolution, exactly as for the per-turn ceiling.
 * `user_credit_settings.daily_spend_ceiling_usd` is `numeric(10,2)`, so a
 * sub-cent figure rounds to `0.00` on the way in and reads back as "unset" to
 * every reader (`v > 0`): the save would report success while the platform
 * default quietly governed.
 *
 * A user may set a LOWER ceiling than the default — self-protection is the
 * point of the knob — but never 0, which only ops can do fleet-wide.
 */
export const DAILY_SPEND_CEILING_MIN_USD = 0.01;

/**
 * The most a user may set their per-TASK budget to, in USD — the task-lifetime
 * cap behind `user_credit_settings.max_task_budget_usd`. $100 is the founder's
 * hard ceiling on EVERY settable limit (2026-08-27): no user knob, on any axis,
 * accepts more. All three MAX constants above deliberately agree.
 *
 * ONE DOCUMENTED EXCEPTION (founder, 2026-10-15): an agent's monthly budget
 * (wallet/constants.ts AGENT_BUDGET_MAX_MINOR, ₹1,00,000 or $1,000) also pays for
 * purchases such as flights, so it is bounded there, not here.
 */
export const TASK_BUDGET_MAX_USD = 100;

/**
 * The smallest per-task budget a user may set, in USD — the column's own
 * resolution (`numeric(10,2)`), same reasoning as the two MIN constants above:
 * a sub-cent figure would round to 0.00 and read back as "unset".
 */
export const TASK_BUDGET_MIN_USD = 0.01;

/**
 * Server defaults
 */
export const SERVER_DEFAULTS = {
  PORT: 3005,
  HOST: '0.0.0.0',
  CORS_ORIGIN: 'http://localhost:3000',
} as const;

/**
 * Agent defaults
 *
 * MODEL is used everywhere: production (passed via runtime envs) and local dev.
 * Sonnet 5.5 is the default model (Sonnet 5 retired 2026-09-29). Override via MODEL
 * env var on Railway.
 */
export const AGENT_DEFAULTS = {
  MODEL: 'claude-sonnet-5-5',
  MAX_TURNS: 100,
  PERMISSION_MODE: 'bypassPermissions',
  AGENT_ID: 'general',
} as const;

/**
 * Socket.io configuration
 *
 * Tuned for cross-region reliability (Blaxel sandbox ↔ Railway server)
 */
export const SOCKET_CONFIG = {
  // Socket.io ping/pong (transport level)
  PING_TIMEOUT: 60000, // 1 minute - time to wait for pong before considering connection dead
  PING_INTERVAL: 15000, // 15s - reduced from 25s to catch dead connections faster

  // Reconnection settings
  RECONNECTION_ATTEMPTS: 15, // More attempts for cross-region instability
  RECONNECTION_DELAY: 500, // Initial delay
  RECONNECTION_DELAY_MAX: 10000, // Max delay with exponential backoff
  RECONNECTION_RANDOMIZATION: 0.5, // Jitter to prevent thundering herd

  // Timeouts
  AGENT_TIMEOUT: 180000, // 3 minutes for long SDK operations

  // NOTE: Custom heartbeat removed - Socket.io ping/pong is sufficient
  // Blaxel's automatic standby handles idle management (preserves state, 25ms resume)

  // Buffer limits
  MAX_HTTP_BUFFER_SIZE: 15 * 1024 * 1024, // 15MB for base64 images

  // Compression
  PER_MESSAGE_DEFLATE: {
    threshold: 1024,
    zlibDeflateOptions: { chunkSize: 16 * 1024 },
    zlibInflateOptions: { chunkSize: 16 * 1024 },
  },

  // Transports - websocket primary, polling fallback
  TRANSPORTS: ['websocket', 'polling'] as const,
} as const;

/**
 * Process management timeouts (in milliseconds)
 */
export const PROCESS_TIMEOUTS = {
  /** Delay after graceful shutdown signal before force kill */
  GRACEFUL_SHUTDOWN_DELAY: 500,
  /** Delay after force kill to ensure process termination */
  FORCE_KILL_DELAY: 100,
  /** Delay before injecting message from staging to SDK */
  MESSAGE_INJECTION_DELAY: 100,
  /** Max time to wait for agent reconnection after restart */
  AGENT_RECONNECT_TIMEOUT: 2000,
  /** Polling interval when waiting for agent reconnection */
  AGENT_RECONNECT_POLL_INTERVAL: 100,
  /** Timeout for SDK interrupt() call (may hang with sub-agents) */
  SDK_INTERRUPT_TIMEOUT: 500,
} as const;

/**
 * Session defaults
 * NOTE: Server-side idle timeout removed - Blaxel sandbox manages its own lifecycle
 * Sandbox goes to standby when WebSocket disconnects (preserves state, 25ms resume)
 */
export const SESSION_DEFAULTS = {
  STATUS: 'active',
  PERMISSION_MODE: 'bypassPermissions',
} as const;

/**
 * Queue limits to prevent memory leaks
 * CRITICAL: These limits prevent unbounded memory growth from message queues
 */
export const QUEUE_LIMITS = {
  /** Maximum number of messages in staging + injected queues combined */
  MAX_QUEUE_SIZE: 50,
  /** Maximum total size of image data in bytes (100MB) */
  MAX_IMAGE_DATA_BYTES: 100 * 1024 * 1024,
  /** Maximum size of a single image in bytes (10MB) */
  MAX_SINGLE_IMAGE_BYTES: 10 * 1024 * 1024,
} as const;

/**
 * Allowed tools for agent
 * Task tool enables SDK subagent delegation
 * Skill tool enables skills loaded from .claude/skills/
 */
export const ALLOWED_TOOLS = [
  // File operations
  'Read',
  'Write',
  'Edit',
  'MultiEdit',
  'NotebookEdit',
  'NotebookRead',
  // Search
  'Glob',
  'Grep',
  'LS',
  // Execution
  'Bash',
  // Web
  'WebSearch',
  'WebFetch',
  // User interaction
  'AskUserQuestion',
  'TodoWrite',
  'TodoRead',
  // Task management (legacy Task* names kept during deprecation window)
  'TaskCreate',
  'TaskGet',
  'TaskList',
  'TaskUpdate',
  // Todo* names (new canonical names)
  'TodoCreate',
  'TodoGet',
  'TodoList',
  'TodoUpdate',
  // Delegation & skills
  'Task',
  'Skill',
  // Plan mode
  'EnterPlanMode',
  'ExitPlanMode',
] as const;

export type AllowedTool = (typeof ALLOWED_TOOLS)[number];
